import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { generateSupportResponse } from '../services/aiService';
import { getStoredConfig } from '../hooks/useChatWidgetSync';
import { useTheme } from '../contexts/ThemeContext';

// Strip markdown formatting from AI responses for clean plain-text display
function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[\-\*]\s+/gm, '• ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .trim();
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

/* ──────────────────────────────────────────────
 * Headphone Icon — clean golden headset icon
 * with a circular surround (matches reference)
 * ────────────────────────────────────────────── */
const HeadphoneIcon = ({ size = 24, color = '#c27b37', className }: { size?: number; color?: string; className?: string }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="11" stroke={color} strokeWidth="1.5" fill="none" />
    <path d="M6 15v-3a6 6 0 1 1 12 0v3" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <rect x="4" y="13" width="3" height="5" rx="1.5" fill={color} />
    <rect x="17" y="13" width="3" height="5" rx="1.5" fill={color} />
  </svg>
);

/* ──────────────────────────────────────────────
 * Smaller header icon (larger, more detailed)
 * ────────────────────────────────────────────── */
const HeaderHeadphoneIcon = ({ size = 38, color = '#c27b37' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="19" cy="19" r="17.5" stroke={color} strokeWidth="1.5" fill="none" opacity="0.25" />
    <circle cx="19" cy="19" r="14" stroke={color} strokeWidth="1.5" fill="none" opacity="0.5" />
    <path d="M10 23v-4a9 9 0 1 1 18 0v4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <rect x="7" y="20.5" width="4" height="6.5" rx="2" fill={color} />
    <rect x="27" y="20.5" width="4" height="6.5" rx="2" fill={color} />
  </svg>
);

export default function SupportWidget() {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputVal, setInputVal] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  const [config, setConfig] = useState<any>(null);

  const activeLang = i18n.language.substring(0, 2);
  
  const greetingLabels: Record<string, string> = {
    en: 'Hello! I am your Support Assistant. How can I help you today?',
    de: 'Hallo! Ich bin dein IdeaVerdict Support-Assistent. Wie kann ich helfen?',
    fr: 'Bonjour ! Je suis votre assistant de support. Comment puis-je vous aider aujourd\'hui ?',
    es: '¡Hola! Soy tu asistente de soporte. ¿Cómo puedo ayudarte hoy?',
    pt: 'Olá! Sou o seu assistente de suporte. Como posso ajudar hoje?',
    it: 'Ciao! Sono il tuo assistente di supporto. Come posso aiutarti oggi?',
    ru: 'Здравствуйте! Я ваш помощник службы поддержки. Чем могу помочь сегодня?',
    tr: 'Merhaba! Ben Destek Asistanınızım. Bugün size nasıl yardımcı olabilirim?',
    pl: 'Cześć! Jestem Twoim asystentem wsparcia. W czym mogę dzisiaj pomóc?',
    ar: 'مرحباً! أنا مساعد الدعم الخاص بك. كيف يمكنني مساعدتك اليوم؟',
    hi: 'नमस्ते! मैं आपका सहायता सहायक हूँ। आज मैं आपकी कैसे मदद कर सकता हूँ?',
    ja: 'こんにちは！サポートアシスタントです。本日はどのようなご用件でしょうか？',
    ko: '안녕하세요! 지원 어시스턴트입니다. 오늘 어떤 도움이 필요하신가요?',
    zh: '您好！我是您的支持助手。请问今天有什么我可以帮您的？',
    th: 'สวัสดี! ฉันคือผู้ช่วยฝ่ายสนับสนุนของคุณ วันนี้ฉันจะช่วยเหลือคุณได้อย่างไร?',
    vi: 'Xin chào! Tôi là Trợ lý hỗ trợ của bạn. Hôm nay tôi có thể giúp gì cho bạn?',
    sv: 'Hej! Jag är din supportassistent. Hur kan jag hjälpa dig idag?',
    no: 'Hei! Jeg er din supportassistent. Hvordan kan jeg hjelpe deg i dag?',
    id: 'Halo! Saya Asisten Dukungan Anda. Ada yang bisa saya bantu hari ini?',
    sq: 'Përshëndetje! Unë jam Asistenti juaj i Mbështetjes. Si mund t\'ju ndihmoj sot?'
  };

  useEffect(() => {
    const c = getStoredConfig();
    if (c) setConfig(c);
    const handleUpdate = () => {
      const updated = getStoredConfig();
      if (updated) setConfig(updated);
    };
    window.addEventListener('chat_widget_updated', handleUpdate);
    return () => window.removeEventListener('chat_widget_updated', handleUpdate);
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = config?.texts?.[activeLang]?.emptyChatMessage || greetingLabels[activeLang] || greetingLabels['en'];
      setMessages([{ role: 'assistant', content: greeting }]);
    }
  }, [isOpen, messages.length, activeLang, config]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Poll speechSynthesis.speaking to track TTS state
  useEffect(() => {
    if (!isSpeaking) return;
    const interval = setInterval(() => {
      if (!window.speechSynthesis.speaking) {
        setIsSpeaking(false);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [isSpeaking]);

  const handleSendText = async (text: string) => {
    if (!text.trim() || isTyping) return;
    const newMsgs: Message[] = [...messages, { role: 'user', content: text }];
    // Add user message + empty assistant placeholder for streaming
    const streamingMsgs: Message[] = [...newMsgs, { role: 'assistant', content: '' }];
    setMessages(streamingMsgs);
    setInputVal('');
    setIsTyping(true);
    const responseText = await generateSupportResponse(
      text,
      newMsgs.filter(m => m.role === 'user' || m.role === 'assistant'),
      (fullText: string) => {
        // Update the last (assistant) message in-place as chunks arrive
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: stripMarkdown(fullText) };
          return updated;
        });
      }
    );
    setIsTyping(false);
    // Finalize with the complete response (in case streaming didn't fire)
    setMessages(prev => {
      const updated = [...prev];
      updated[updated.length - 1] = { role: 'assistant', content: stripMarkdown(responseText) };
      return updated;
    });
  };

  // ---- Voice: Start Listening ----
  const startListening = () => {
    stopSpeaking();
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRec) {
      alert(t('support.voiceNotSupported', 'Voice recognition is not supported in this browser.'));
      return;
    }
    const recognition = new SpeechRec();
    recognition.lang = i18n.language;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = async (event: any) => {
      const transcript = event.results[0][0].transcript;
      if (transcript) {
        setIsListening(false);
        recognitionRef.current = null;
        const newMsgs: Message[] = [...messages, { role: 'user', content: transcript }];
        const streamingMsgs: Message[] = [...newMsgs, { role: 'assistant', content: '' }];
        setMessages(streamingMsgs);
        setIsTyping(true);
        const responseText = await generateSupportResponse(
          transcript,
          newMsgs.filter(m => m.role === 'user' || m.role === 'assistant'),
          (fullText: string) => {
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: stripMarkdown(fullText) };
              return updated;
            });
          }
        );
        setIsTyping(false);
        const cleanResponse = stripMarkdown(responseText);
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: cleanResponse };
          return updated;
        });
        speakText(cleanResponse);
      }
    };
    recognition.onerror = () => { setIsListening(false); recognitionRef.current = null; };
    recognition.onend = () => { setIsListening(false); recognitionRef.current = null; };
    recognition.start();
  };

  const stopListening = () => {
    if (recognitionRef.current) { recognitionRef.current.abort(); recognitionRef.current = null; }
    setIsListening(false);
  };

  const speakText = (text: string) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = i18n.language;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
    setIsSpeaking(true);
  };

  const stopSpeaking = () => { window.speechSynthesis?.cancel(); setIsSpeaking(false); };
  const stopAll = () => { stopListening(); stopSpeaking(); };

  
  /* ── Multi-language labels ── */
  const defaultLabels: Record<string, string> = {
    en: 'Talk with Us', de: 'Mit uns sprechen', fr: 'Parlez-nous',
    es: 'Habla con nosotros', pt: 'Fale conosco', it: 'Parla con noi',
    ru: 'Свяжитесь с нами', tr: 'Bizimle Konuşun', pl: 'Porozmawiaj z nami',
    ar: 'تحدث معنا', hi: 'हमसे बात करें', ja: 'お問い合わせ',
    ko: '문의하기', zh: '联系我们', th: 'พูดคุยกับเรา',
    vi: 'Noi chuyện với chúng tôi', sv: 'Prata med oss', no: 'Snakk med oss',
    id: 'Bicara dengan Kami', sq: 'Flisni me ne'
  };
  const fallbackLabel = defaultLabels[activeLang] || 'Talk with Us';
  const mainLabel = (config?.texts?.[activeLang]?.mainLabel) || fallbackLabel;

  const readyToAssistLabels: Record<string, string> = {
    en: 'Ready to assist', de: 'Bereit zu helfen', fr: 'Prêt à aider',
    es: 'Listo para ayudar', pt: 'Pronto para ajudar', it: 'Pronto ad aiutare',
    ru: 'Готов помочь', tr: 'Yardıma hazır', pl: 'Gotowy do pomocy',
    ar: 'جاهز للمساعدة', hi: 'मदद के लिए तैयार', ja: '対応準備完了',
    ko: '도움 준비 완료', zh: '随时为您服务', th: 'พร้อมช่วยเหลือ',
    vi: 'Sẵn sàng hỗ trợ', sv: 'Redo att hjälpa', no: 'Klar til å hjelpe',
    id: 'Siap membantu', sq: 'Gati për ndihmë'
  };
  const readyToAssistText = readyToAssistLabels[activeLang] || 'Ready to assist';

  const placeholderLabels: Record<string, string> = {
    en: 'Type your message...', de: 'Schreibe eine Nachricht...', fr: 'Écrivez votre message...',
    es: 'Escribe tu mensaje...', pt: 'Digite sua mensagem...', it: 'Scrivi il tuo messaggio...',
    ru: 'Введите сообщение...', tr: 'Mesajınızı yazın...', pl: 'Wpisz swoją wiadomość...',
    ar: 'اكتب رسالتك...', hi: 'अपना संदेश लिखें...', ja: 'メッセージを入力...',
    ko: '메시지를 입력하세요...', zh: '输入您的消息...', th: 'พิมพ์ข้อความ...',
    vi: 'Nhập tin nhắn...', sv: 'Skriv ditt meddelande...', no: 'Skriv meldingen din...',
    id: 'Ketik pesan Anda...', sq: 'Shkruani mesazhin tuaj...'
  };
  const placeholder = placeholderLabels[activeLang] || 'Type your message...';

  const listeningLabels: Record<string, string> = {
    en: 'Listening...', de: 'Ich höre zu...', fr: 'Écoute en cours...', es: 'Escuchando...',
    pt: 'Ouvindo...', it: 'Ascolto...', ru: 'Слушаю...', tr: 'Dinleniyor...',
    ar: 'أستمع...', hi: 'सुन रहा हूँ...', ja: '聞いています...', ko: '듣는 중...',
    zh: '正在聆听...', th: 'กำลังฟัง...', vi: 'Đang nghe...', pl: 'Słucham...',
    sv: 'Lyssnar...', no: 'Lytter...', id: 'Mendengarkan...', sq: 'Duke dëgjuar...'
  };
  const speakingLabels: Record<string, string> = {
    en: 'Speaking...', de: 'Spricht...', fr: 'Parle...', es: 'Hablando...',
    pt: 'Falando...', it: 'Parla...', ru: 'Говорю...', tr: 'Konuşuyor...',
    ar: 'يتحدث...', hi: 'बोल रहा है...', ja: '話しています...', ko: '말하는 중...',
    zh: '正在说话...', th: 'กำลังพูด...', vi: 'Đang nói...', pl: 'Mówię...',
    sv: 'Talar...', no: 'Snakker...', id: 'Berbicara...', sq: 'Duke folur...'
  };

  // Theme-aware tokens (template emerald palette)
  const ACCENT = '#10b981';        // emerald-500
  const ACCENT_HOVER = '#34d399';  // emerald-400
  const accentHsl = ACCENT;        // legacy alias kept

  // Voice button state
  const voiceActive = isListening || isSpeaking;
  const voiceBtnColor = isListening ? '#ef4444' : isSpeaking ? '#3b82f6' : ACCENT;

  const handleVoiceClick = () => {
    if (isListening) { stopListening(); }
    else if (isSpeaking) { stopSpeaking(); }
    else { startListening(); }
  };

  // ── Theme-aware surfaces (template emerald + slate) ──────────────
  const surfaceBg     = isDark ? 'bg-slate-900' : 'bg-white';
  const surfaceBorder = isDark ? 'border-white/10' : 'border-slate-200';
  const headerSubBg   = isDark ? 'bg-slate-900' : 'bg-white';
  const inputBg       = isDark ? 'bg-slate-800 text-white border-white/10 placeholder-slate-500' : 'bg-white text-slate-900 border-slate-200 placeholder-slate-400';
  const assistantBubbleBg = isDark ? 'bg-slate-800 text-slate-100' : 'bg-slate-100 text-slate-800';
  const titleText     = isDark ? 'text-white' : 'text-slate-900';
  const iconBtnHover  = isDark ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100';
  const messagesBg    = isDark ? 'bg-slate-900' : 'bg-white';
  const inputBarBg    = isDark ? 'bg-slate-900 border-white/5' : 'bg-white border-slate-100';
  const scrollbarThumb = isDark ? '#334155' : '#cbd5e1';
  const scrollbarThumbHover = isDark ? '#10b981' : '#94a3b8';

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          #getverdict-support-widget { display: flex; }
        }
        @keyframes soundWave { 0%, 100% { transform: scaleY(0.4); } 50% { transform: scaleY(1); } }
        @keyframes listeningPulse {
          0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
          70% { box-shadow: 0 0 0 12px rgba(239, 68, 68, 0); }
          100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
        @keyframes speakingPulse {
          0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
          70% { box-shadow: 0 0 0 12px rgba(59, 130, 246, 0); }
          100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
        }
        @keyframes widgetSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes dotBounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-5px); }
        }
        @keyframes fabPulseEmerald {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.45), 0 8px 24px rgba(16,185,129,0.35); }
          50% { box-shadow: 0 0 0 8px rgba(16,185,129,0.0), 0 8px 24px rgba(16,185,129,0.45); }
        }
        .voice-bar { width: 3px; border-radius: 999px; animation: soundWave 0.6s ease-in-out infinite; }
        .support-chat-window {
          animation: widgetSlideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .support-chat-window .msg-scroll::-webkit-scrollbar { width: 4px; }
        .support-chat-window .msg-scroll::-webkit-scrollbar-track { background: transparent; }
        .support-chat-window .msg-scroll::-webkit-scrollbar-thumb { background: ${scrollbarThumb}; border-radius: 99px; }
        .support-chat-window .msg-scroll::-webkit-scrollbar-thumb:hover { background: ${scrollbarThumbHover}; }
      `}</style>

      <div id="getverdict-support-widget" className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9999] flex flex-col items-end gap-3">

        {/* ═══════════════════════════════════════
            Chat Window (Opened State)
            ═══════════════════════════════════════ */}
        {isOpen && (
          <div
            className={`support-chat-window w-[calc(100vw-2rem)] sm:w-[380px] rounded-2xl border ${surfaceBg} ${surfaceBorder} flex flex-col overflow-hidden origin-bottom-right`}
            style={{
              maxHeight: 'min(560px, 80vh)',
              boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.18)'
            }}
          >
            {/* ── Header (emerald gradient like template) ── */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                  <i className="ri-customer-service-2-line text-white text-xl" />
                </div>
                <div className="min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{mainLabel}</div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 bg-emerald-300 rounded-full animate-pulse" />
                    <span className="text-emerald-100 text-xs truncate">{readyToAssistText}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => { stopAll(); setMessages([{ role: 'assistant', content: config?.texts?.[activeLang]?.emptyChatMessage || greetingLabels[activeLang] || greetingLabels['en'] }]); }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-all"
                  title="Reset"
                >
                  <i className="ri-refresh-line text-base" />
                </button>
                <button
                  onClick={() => { stopAll(); setIsOpen(false); }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-all"
                  title="Close"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
            </div>

            {/* ── Listening / Speaking Banner ── */}
            {(isListening || isSpeaking) && (
              <div
                className="mx-4 mt-3 px-3 py-2 rounded-xl flex items-center justify-between transition-all duration-300"
                style={{
                  backgroundColor: isListening
                    ? (isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2')
                    : (isDark ? 'rgba(59,130,246,0.1)' : '#eff6ff'),
                  border: `1px solid ${isListening ? (isDark ? 'rgba(239,68,68,0.3)' : '#fecaca') : (isDark ? 'rgba(59,130,246,0.3)' : '#bfdbfe')}`
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-[3px] h-5">
                    {[0, 1, 2, 3, 4].map(i => (
                      <div
                        key={i}
                        className="voice-bar"
                        style={{ height: '16px', backgroundColor: isListening ? '#ef4444' : '#3b82f6', animationDelay: `${i * 0.12}s` }}
                      />
                    ))}
                  </div>
                  <span className="text-xs font-medium" style={{ color: isListening ? '#ef4444' : '#3b82f6' }}>
                    {isListening
                      ? (listeningLabels[activeLang] || 'Listening...')
                      : (speakingLabels[activeLang] || 'Speaking...')}
                  </span>
                </div>
                <button
                  onClick={isListening ? stopListening : stopSpeaking}
                  className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-110 text-white"
                  style={{ backgroundColor: isListening ? '#ef4444' : '#3b82f6' }}
                >
                  <i className="ri-stop-fill text-xs" />
                </button>
              </div>
            )}

            {/* ── Messages Area ── */}
            <div className={`msg-scroll px-4 py-4 flex-1 overflow-y-auto flex flex-col gap-3 ${messagesBg}`} style={{ minHeight: '220px' }}>
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[82%] px-4 py-2.5 text-[14px] leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-emerald-500 text-white rounded-2xl rounded-br-md'
                        : `${assistantBubbleBg} rounded-2xl rounded-bl-md`
                    }`}
                  >
                    {msg.content || (msg.role === 'assistant' && isTyping) ? (
                      <>
                        {msg.content}
                        <span className={isTyping && i === messages.length - 1 && msg.role === 'assistant' ? 'inline-block w-[2px] h-[14px] bg-current opacity-60 ml-0.5 align-text-bottom animate-pulse' : 'hidden'} />
                      </>
                    ) : null}
                  </div>
                </div>
              ))}

              {isTyping && messages[messages.length - 1]?.role !== 'assistant' && (
                <div className="flex justify-start">
                  <div className={`px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-1.5 ${assistantBubbleBg}`}>
                    {[0, 1, 2].map(i => (
                      <div key={i} className="w-1.5 h-1.5 bg-current opacity-50 rounded-full" style={{ animation: `dotBounce 1.2s ease-in-out ${i * 0.15}s infinite` }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* ── Input Area ── */}
            <div className={`px-3 py-3 border-t ${inputBarBg}`}>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={inputVal}
                  onChange={e => setInputVal(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSendText(inputVal)}
                  placeholder={placeholder}
                  disabled={isListening}
                  className={`flex-1 min-w-0 px-4 py-2.5 text-sm rounded-xl border focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${inputBg}`}
                />

                {/* Send Button */}
                <button
                  onClick={() => handleSendText(inputVal)}
                  disabled={!inputVal.trim() || isTyping || isListening}
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
                    inputVal.trim() && !isTyping && !isListening
                      ? 'bg-emerald-500 hover:bg-emerald-400 text-white cursor-pointer'
                      : isDark
                        ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                        : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  }`}
                  style={inputVal.trim() && !isTyping && !isListening ? { boxShadow: '0 0 14px rgba(16,185,129,0.35)' } : undefined}
                >
                  <i className="ri-send-plane-fill text-base" />
                </button>

                {/* Voice Button */}
                <button
                  onClick={handleVoiceClick}
                  disabled={isTyping}
                  className="w-10 h-10 rounded-xl flex items-center justify-center transition-all relative text-white flex-shrink-0"
                  style={{
                    backgroundColor: voiceBtnColor,
                    animation: isListening ? 'listeningPulse 1.5s infinite' : isSpeaking ? 'speakingPulse 1.5s infinite' : 'none',
                  }}
                  title={isListening ? 'Stop' : isSpeaking ? 'Stop' : 'Voice'}
                >
                  <i className={`${isListening ? 'ri-mic-fill' : isSpeaking ? 'ri-volume-up-fill' : 'ri-mic-line'} text-base`} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════
            Floating Toggle Button (template style)
            ═══════════════════════════════════════ */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Close support' : mainLabel}
          className="w-14 h-14 flex items-center justify-center bg-emerald-500 hover:bg-emerald-400 rounded-full cursor-pointer transition-all duration-200 hover:scale-110 active:scale-95"
          style={{
            boxShadow: '0 0 24px rgba(16,185,129,0.4), 0 8px 24px rgba(0,0,0,0.15)',
            animation: isOpen ? 'none' : 'fabPulseEmerald 3.5s ease-in-out infinite',
          }}
        >
          <i className={`${isOpen ? 'ri-close-line' : 'ri-customer-service-2-line'} text-white text-2xl transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
        </button>
      </div>
    </>
  );
}
